import React from 'react';
import { Card, Title, BarChart, Text, Subtitle, Grid, Col, Flex, Legend, Color, Tab, TabGroup, TabList, TabPanel, TabPanels } from '@tremor/react';

export default function EducationFundingComparisonChart() {
  // Data for the three scenarios at different ages
  const scenarioData = [
    {
      age: 18,
      respOnly: 88619,
      parWlOnly: 42000,
      hybrid: 130619,
    },
    {
      age: 25,
      respOnly: 88619, // RESP stays the same after education (assuming used/closed)
      parWlOnly: 65000,
      hybrid: 153619,
    },
    {
      age: 40,
      respOnly: 88619, // RESP stays the same
      parWlOnly: 190000,
      hybrid: 278619,
    },
    {
      age: 65,
      respOnly: 88619, // RESP stays the same
      parWlOnly: 820000,
      hybrid: 908619,
    },
  ];

  // Data for the flexibility comparison
  const flexibilityData = [
    {
      category: "Education",
      respOnly: 100,
      parWlOnly: 70,
      hybrid: 100,
    },
    {
      category: "Home Purchase",
      respOnly: 30,
      parWlOnly: 90,
      hybrid: 90,
    },
    {
      category: "Business Startup",
      respOnly: 20,
      parWlOnly: 90,
      hybrid: 90,
    },
    {
      category: "Retirement",
      respOnly: 10,
      parWlOnly: 95,
      hybrid: 95,
    },
    {
      category: "Estate Transfer",
      respOnly: 5,
      parWlOnly: 100,
      hybrid: 100,
    },
  ];

  // Data for the tax efficiency comparison
  const taxEfficiencyData = [
    {
      phase: "Contribution",
      respOnly: "After-tax dollars (no deduction)",
      parWlOnly: "After-tax dollars (no deduction)",
      hybrid: "After-tax dollars (no deduction)",
    },
    {
      phase: "Growth",
      respOnly: "Tax-deferred",
      parWlOnly: "Tax-deferred",
      hybrid: "Tax-deferred",
    },
    {
      phase: "Withdrawal (Education)",
      respOnly: "EAPs taxed in student's hands (often low/no tax)",
      parWlOnly: "Policy loans (usually tax-free)",
      hybrid: "Both options available",
    },
    {
      phase: "Withdrawal (Other)",
      respOnly: "AIPs: Tax + 20% penalty*",
      parWlOnly: "Policy loans (usually tax-free)",
      hybrid: "Favor PAR WL for non-education",
    },
    {
      phase: "Transfer to Next Generation",
      respOnly: "N/A (Plan closes)",
      parWlOnly: "Tax-free death benefit",
      hybrid: "Partial tax-free transfer",
    },
  ];

  return (
    <Card className="mt-8 mb-8 bg-purple-50 border border-purple-100">
      <Title className="text-purple-800 mb-2">Strategy Comparison: RESP vs. PAR WL vs. Hybrid</Title>
      <Subtitle className="text-purple-600 mb-4">
        Comparing the three approaches across different dimensions
      </Subtitle>

      <TabGroup>
        <TabList className="mt-4">
          <Tab>Value Over Time</Tab>
          <Tab>Flexibility</Tab>
          <Tab>Tax Efficiency</Tab>
        </TabList>
        
        <TabPanels>
          <TabPanel>
            <div className="mt-4">
              <Flex className="mt-4 mb-2 justify-center">
                <Legend
                  className="max-w-lg"
                  categories={["RESP Only", "PAR WL Only", "Hybrid Approach"]}
                  colors={["blue", "green", "purple"]}
                />
              </Flex>
              
              <BarChart
                className="h-72 mt-4"
                data={scenarioData}
                index="age"
                categories={["respOnly", "parWlOnly", "hybrid"]}
                colors={["blue", "green", "purple"]}
                valueFormatter={(number) => `$${number.toLocaleString()}`}
                stack={false}
                layout="vertical"
                showLegend={false}
                showGridLines={true}
                showAnimation={true}
              />
              
              <Text className="text-xs text-gray-500 mt-4">
                Note: This chart shows the projected value of each strategy at different ages. The RESP value remains constant after age 18
                as it's typically used for education. The PAR WL policy continues to grow throughout life. The hybrid approach combines the benefits of both.
              </Text>
            </div>
          </TabPanel>
          
          <TabPanel>
            <div className="mt-4">
              <Subtitle className="text-purple-700 mb-4">
                Flexibility for Different Financial Needs (100 = Maximum Flexibility)
              </Subtitle>
              
              <BarChart
                className="h-72 mt-4"
                data={flexibilityData}
                index="category"
                categories={["respOnly", "parWlOnly", "hybrid"]}
                colors={["blue", "green", "purple"]}
                valueFormatter={(number) => `${number}%`}
                showLegend={false}
                showGridLines={true}
                showAnimation={true}
              />
              
              <Flex className="mt-4 mb-2 justify-center">
                <Legend
                  className="max-w-lg"
                  categories={["RESP Only", "PAR WL Only", "Hybrid Approach"]}
                  colors={["blue", "green", "purple"]}
                />
              </Flex>
              
              <Text className="text-xs text-gray-500 mt-4">
                Note: This chart illustrates the relative flexibility of each strategy for different financial needs.
                Higher percentages indicate greater flexibility for that particular need. The RESP excels for education but has
                limited flexibility for other purposes, while PAR WL and the hybrid approach offer more versatility.
              </Text>
            </div>
          </TabPanel>
          
          <TabPanel>
            <div className="mt-4">
              <Subtitle className="text-purple-700 mb-4">
                Tax Efficiency Across Different Life Phases
              </Subtitle>
              
              <Card className="bg-white">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phase
                      </th>
                      <th className="px-4 py-3 bg-blue-50 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                        RESP Only
                      </th>
                      <th className="px-4 py-3 bg-green-50 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                        PAR WL Only
                      </th>
                      <th className="px-4 py-3 bg-purple-50 text-left text-xs font-medium text-purple-700 uppercase tracking-wider">
                        Hybrid Approach
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {taxEfficiencyData.map((item, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {item.phase}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 bg-blue-50">
                          {item.respOnly}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 bg-green-50">
                          {item.parWlOnly}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 bg-purple-50">
                          {item.hybrid}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
              
              <Text className="text-xs text-gray-500 mt-4">
                * AIPs (Accumulated Income Payments) from an RESP can be transferred to an RRSP (up to $50,000) if you have contribution room,
                avoiding the 20% penalty. The hybrid approach gives you the best of both worlds for tax planning.
              </Text>
            </div>
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </Card>
  );
}
